/**
 * CoachReviewsSection — affichage public premium des avis d'un coach.
 *
 * Affiche :
 *   • Note moyenne + nombre d'avis (header)
 *   • Distribution des notes 5→1 en barres de progression dorées
 *   • Liste des avis vérifiés (5 initialement + pagination)
 *   • Badge « Avis vérifié » sur chaque avis
 *
 * Endpoint : GET /api/marketplace/coaches/:id/reviews
 */
import React, { useEffect, useState } from 'react';
import { Star, ShieldCheck, MessageSquare, ChevronDown, Loader2 } from 'lucide-react';
import { getApiUrl } from '../config/environment';
import { formatDateFR } from '../utils/formatFR';

const API = getApiUrl();
const PAGE_SIZE = 5;

export default function CoachReviewsSection({ coachUserId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API}/marketplace/coaches/${coachUserId}/reviews?limit=${PAGE_SIZE}&skip=0`
        );
        if (res.ok) setData(await res.json());
      } finally { setLoading(false); }
    })();
  }, [coachUserId]);

  const loadMore = async () => {
    if (!data || data.reviews.length >= data.count) return;
    setLoadingMore(true);
    try {
      const res = await fetch(
        `${API}/marketplace/coaches/${coachUserId}/reviews?limit=50&skip=${data.reviews.length}`
      );
      if (res.ok) {
        const more = await res.json();
        setData(prev => ({ ...prev, reviews: [...prev.reviews, ...more.reviews] }));
        setExpanded(true);
      }
    } finally { setLoadingMore(false); }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-[#6D28D9]" />
      </div>
    );
  }
  if (!data || data.count === 0) {
    return (
      <div className="text-center py-8 text-sm text-[#6B7280]" data-testid="coach-reviews-empty">
        <MessageSquare className="w-6 h-6 mx-auto mb-2 text-gray-300" />
        Pas encore d'avis — soyez le premier à laisser un retour après votre séance.
      </div>
    );
  }

  const total = data.count;
  const maxInDistribution = Math.max(...Object.values(data.distribution), 1);

  return (
    <section className="space-y-6" data-testid="coach-reviews-section">
      {/* Header — big rating + distribution */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-[#FFF6E5] via-white to-[#FBEAFF] border border-[#E8E0F2]">
        {/* Average */}
        <div className="text-center sm:text-left flex sm:block items-center gap-4">
          <div>
            <div className="text-5xl sm:text-6xl font-bold text-[#1C1147] leading-none mb-1"
              data-testid="coach-reviews-avg">
              {data.average.toFixed(1)}
            </div>
            <div className="flex items-center justify-center sm:justify-start gap-0.5 mb-1">
              {[1, 2, 3, 4, 5].map(n => (
                <Star
                  key={n}
                  className={`w-4 h-4 ${
                    n <= Math.round(data.average)
                      ? 'fill-[#D4AF37] text-[#D4AF37]'
                      : 'text-gray-200'
                  }`}
                />
              ))}
            </div>
            <div className="text-xs text-[#4B3869]">
              {total} avis vérifié{total > 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Distribution */}
        <div className="space-y-1.5">
          {[5, 4, 3, 2, 1].map(n => {
            const count = data.distribution[String(n)] || 0;
            const pct = (count / maxInDistribution) * 100;
            return (
              <div key={n} className="flex items-center gap-2 text-xs">
                <span className="w-3 text-[#4B3869] font-semibold">{n}</span>
                <Star className="w-3 h-3 fill-[#D4AF37] text-[#D4AF37] flex-shrink-0" />
                <div className="flex-1 h-2 rounded-full bg-white border border-gray-100 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#D4AF37] to-[#C49B2A] transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="w-6 text-right text-[#6B7280] tabular-nums">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Reviews list */}
      <div className="space-y-3">
        {data.reviews.map((r, i) => (
          <article
            key={i}
            data-testid={`coach-review-${i}`}
            className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6D28D9] to-[#1C1147] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {r.author_initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-sm font-semibold text-[#1C1147]">{r.author_name}</span>
                  <span
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 font-bold"
                    title="Avis posté après une séance réelle"
                  >
                    <ShieldCheck className="w-3 h-3" /> Avis vérifié
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map(n => (
                      <Star
                        key={n}
                        className={`w-3.5 h-3.5 ${
                          n <= r.rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-[11px] text-[#6B7280]">
                    Séance du {formatDateFR(r.session_date)}
                  </span>
                </div>
                {r.comment && (
                  <p className="text-sm text-[#4B3869] leading-relaxed">{r.comment}</p>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* Load more */}
      {!expanded && data.reviews.length < total && (
        <button
          onClick={loadMore}
          disabled={loadingMore}
          data-testid="coach-reviews-load-more"
          className="w-full py-2.5 rounded-full text-sm font-semibold text-[#6D28D9] bg-[#F5F3FF] border border-violet-200 hover:bg-[#EDE9FE] active:scale-[0.99] transition-all flex items-center justify-center gap-2"
        >
          {loadingMore ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>Voir les {total - data.reviews.length} autres avis <ChevronDown className="w-4 h-4" /></>
          )}
        </button>
      )}
    </section>
  );
}
